import React, { useState } from 'react';

import { ToggleComponent } from 'src/common/form/toggle/component';
import { JourneyAlias } from 'src/common/interfaces/journey/alias.interface';
import { ShareAdditionalLinkItemHoverMenu } from './item-hover-menu';
import ClockStrongIcon from 'src/monet/icons/ClockStrongIcon';
import { CopyStrongIcon, LinkStrongIcon, SecureStrongIcon } from 'src/monet/icons';
import { apiUpdateJourneyAlias } from 'src/utils/journeyApi';
import { ShareContentCommonProps } from '../../content';
import { TooltipComponent } from 'src/common/tooltip/component';
import PersonalizatonLogo from '../../../personalization/personalization-logo';
import { generateJourneyAliasShareUrl } from 'src/utils/journey';
import { useShareThumbnailCodeStore } from '../../../thumbnail-code/store';
import { useSharePanelStore } from 'src/common/store/share.store';
import { useCurrentOrganization } from 'src/store/organization';

interface Props extends ShareContentCommonProps {
  alias: JourneyAlias;
  onCopyLinkClick: (alias: JourneyAlias) => void;
  onEditLinkClick: (alias: JourneyAlias) => () => void;
  onDeleteConfirm: (alias: JourneyAlias) => () => void;
}

const SharePanelLinkListItem = ({ alias, onCopyLinkClick, onEditLinkClick, onDeleteConfirm, onAliasUpdate }: Props) => {
  const currentOrganization = useCurrentOrganization((state) => state.currentOrganization);
  const journey = useSharePanelStore((state) => state.sharedJourney);
  const generateThumbnailCode = useShareThumbnailCodeStore((state) => state.generateThumbnailCode);
  const [checked, setChecked] = useState(alias.enabled);

  const onAliasToggleChanged = async (value: boolean) => {
    setChecked(!checked);
    try {
      const response = await apiUpdateJourneyAlias(alias.uuid, { enabled: value });
      onAliasUpdate(response, null);
    } catch (e) {
      setChecked(checked);
    }
  };

  const onItemRowClick = () => {
    onCopyLinkClick(alias);
  };

  const onCopyThumbnailLinkClicked = async () => {
    const shareUrl = generateJourneyAliasShareUrl(currentOrganization, alias.slug);
    await generateThumbnailCode(journey?.uuid || '', shareUrl);
  };

  return (
    <div className='group/row flex items-start justify-between space-x-2'>
      <div className='group/link flex flex-1 space-x-2 cursor-pointer' onClick={onItemRowClick}>
        <TooltipComponent placement='top' tooltipContent='Copy link' styling='tooltip' theme='journey'>
          <div>
            {alias.personalization && alias.personalization.logo_url ? (
              <PersonalizatonLogo
                logoUrl={alias.personalization.logo_url}
                containerClasses='group-hover/row:hidden w-5 h-5'
              />
            ) : (
              <LinkStrongIcon className='group-hover/row:hidden' />
            )}
            <CopyStrongIcon className='hidden group-hover/row:block' />
          </div>
        </TooltipComponent>
        <div className='text-bedrock-p-strong text-bedrock-black'>{alias.name}</div>
      </div>
      <div className='flex items-start space-x-2'>
        {(!!alias.expires_at || alias.email_required || alias.has_password) && (
          <div className='flex items-start justify-between space-x-2 group-hover/row:hidden text-bedrock-gray-medium'>
            {alias.expires_at && <ClockStrongIcon />}
            {(alias.email_required || alias.has_password) && <SecureStrongIcon />}
          </div>
        )}
        <ShareAdditionalLinkItemHoverMenu
          onEditLinkClick={onEditLinkClick(alias)}
          onDeleteConfirm={onDeleteConfirm(alias)}
          onCopyThumbnailClicked={onCopyThumbnailLinkClicked}
        />
        <ToggleComponent groupClassName='mt-0.5' checked={checked} onChange={onAliasToggleChanged} />
      </div>
    </div>
  );
};

export default SharePanelLinkListItem;
